import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from './api'
import { Arrow, Check, Footer, Logo, PrimaryLink } from './components'

const fallbackPlans = [
  { code: 'creator', name: 'Creator', tier: 'hd', standardVideos: 100, slotGb: 5, monthlyUsd: 29, yearlyUsd: 290, positioning: 'GOOD' },
  { code: 'studio', name: 'Studio', tier: 'hd', standardVideos: 300, slotGb: 5, monthlyUsd: 79, yearlyUsd: 790, positioning: 'BETTER' },
  { code: 'network', name: 'Network', tier: 'hd', standardVideos: 1000, slotGb: 5, monthlyUsd: 249, yearlyUsd: 2490, positioning: 'BEST' },
  { code: 'creator4k', name: 'Creator', tier: '4k', standardVideos: 100, slotGb: 20, monthlyUsd: 59, yearlyUsd: 590, positioning: 'GOOD' },
  { code: 'studio4k', name: 'Studio', tier: '4k', standardVideos: 300, slotGb: 20, monthlyUsd: 159, yearlyUsd: 1590, positioning: 'BETTER' },
  { code: 'network4k', name: 'Network', tier: '4k', standardVideos: 1000, slotGb: 20, monthlyUsd: 499, yearlyUsd: 4990, positioning: 'BEST' },
]

const planBlurb = {
  creator: 'For one growing channel.',
  studio: 'For a larger catalogue.',
  network: 'For teams and channel networks.',
}

export function PricingCards({ compact = false, authenticated = false }) {
  const [plans, setPlans] = useState(fallbackPlans)
  const [billing, setBilling] = useState('monthly')
  const [tier, setTier] = useState('hd')
  useEffect(() => { api('/api/billing/plans').then(data => setPlans(data.plans)).catch(() => {}) }, [])

  const fourK = plans.filter(plan => plan.tier === '4k')
  const shown = tier === '4k' && fourK.length ? fourK : plans.filter(plan => plan.tier !== '4k')

  return (
    <div className={`pricing-v2 ${compact ? 'pricing-v2-compact' : ''}`}>
      {!compact && <div className="pricing-toggles">
        <div className="billing-toggle" role="group" aria-label="Video quality">
          <button className={tier === 'hd' ? 'active' : ''} onClick={() => setTier('hd')}>HD · 1080p</button>
          <button className={tier === '4k' ? 'active' : ''} onClick={() => setTier('4k')}>4K</button>
        </div>
        <div className="billing-toggle" role="group" aria-label="Billing period">
          <button className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>Monthly</button>
          <button className={billing === 'yearly' ? 'active' : ''} onClick={() => setBilling('yearly')}>Yearly · 2 months free</button>
        </div>
      </div>}
      <div className="pricing-grid">
        {shown.map(plan => {
          const yearly = billing === 'yearly'
          const headline = yearly ? Math.floor(plan.yearlyUsd / 12) : plan.monthlyUsd
          const featured = plan.code.startsWith('studio')
          return <article key={plan.code} className={`price-card ${featured ? 'featured' : ''}`}>
            <div className="plan-topline"><span className="plan-name">{plan.name}</span><span>{plan.positioning || ''}</span></div>
            <p>{planBlurb[plan.code.replace('4k', '')] || ''}</p>
            <div className="price"><sup>$</sup><strong>{headline}</strong><span>/month</span></div>
            <small>{yearly ? `$${plan.yearlyUsd.toLocaleString()} billed once a year — 2 months free` : 'Billed monthly. Cancel anytime.'}</small>
            <div className="price-divider"/>
            <ul>
              <li><Check/><span><b>{plan.standardVideos.toLocaleString()} videos</b> in {plan.tier === '4k' ? '4K' : 'HD, up to 1080p'}</span></li>
              <li><Check/><span><b>Daily automatic backups</b> — no “Back up” button</span></li>
              <li><Check/><span>SHA-256 verification after every upload</span></li>
              <li><Check/><span>Private 15-minute restore links</span></li>
              <li><Check/><span>No Google login — we never touch your channel</span></li>
            </ul>
            <PrimaryLink to={`${authenticated ? '/pricing' : '/signup'}?plan=${plan.code}&billing=${billing}`}>Protect my videos <Arrow/></PrimaryLink>
          </article>
        })}
      </div>
      {!compact && <>
        <p className="slot-note">One video is up to 60 minutes ({tier === '4k' ? '20 GB in 4K' : '5 GB in HD'}). Longer videos use one slot per hour, up to 4 hours. Four Shorts share one slot. 4K originals are about four times larger, which is why 4K plans cost about twice HD.</p>
        <p className="guarantee-note"><b>30-day money-back guarantee.</b> Not convinced after your first month? Full refund, and you keep access to download every file for 30 days.</p>
      </>}
    </div>
  )
}

function BackupFlow() {
  return <div className="real-flow" aria-label="How every uploaded file is protected">
    <div className="real-flow-bar"><span/><span>INFLUENCERBACKUPS.COM / PRIVATE VAULT</span><b>REAL PRODUCT FLOW</b></div>
    <div className="real-flow-body">
      <div className="flow-intro"><span className="eyebrow">WHAT HAPPENS TO EVERY FILE</span><h3>One original.<br/>Three checks.<br/>Zero clicks.</h3><p>Set it up once. The uploader runs on its own every day — you never drag files or press a backup button. No customer videos are shown here. Your files stay private.</p></div>
      <div className="flow-track">
        <div className="flow-line"><i/></div>
        <article><span>01</span><div><small>WATCH</small><strong>File stops changing</strong><p>The folder watcher notices a finished render — today’s and every day after.</p></div></article>
        <article><span>02</span><div><small>COPY</small><strong>Direct private upload</strong><p>Resumable parts go from your computer to object storage automatically.</p></div></article>
        <article><span>03</span><div><small>VERIFY</small><strong>SHA-256 matches</strong><p>The server reads the stored copy back before marking it protected.</p></div></article>
      </div>
      <div className="flow-facts"><div><span>BACKUP SCHEDULE</span><strong>Daily · automatic</strong></div><div><span>FINGERPRINT</span><strong>SHA-256</strong></div><div><span>RESTORE ACCESS</span><strong>15 minutes</strong></div><div><span>GOOGLE LOGINS</span><strong>0</strong></div></div>
    </div>
  </div>
}

const vaultRows = [
  { title: 'I quit my job to travel the world', date: 'Jun 2026', status: 'protected' },
  { title: '$50 vs $500 camera — can you tell?', date: 'May 2026', status: 'protected' },
  { title: '100 days living off-grid', date: 'May 2026', status: 'protected' },
  { title: 'My first video (2019, 240 subs)', date: 'Apr 2026', status: 'missing' },
]

function RestoreProof() {
  return <section className="restore-section section" data-reveal>
    <div className="restore-inner">
      <div className="restore-copy">
        <span className="eyebrow">THE DAY IT ACTUALLY HAPPENS</span>
        <h2>Lose the channel tonight.<br/><em>Re-upload everything next week.</em></h2>
        <p>This is the page you open the day YouTube says your channel no longer exists. Every published video is checked against your vault daily, so you always know — today, not after a disaster — which originals are protected and which would be gone forever.</p>
        <ul className="restore-points">
          <li><Check/><span><b>Daily catalog check.</b> New uploads on your channel are matched to protected originals every day.</span></li>
          <li><Check/><span><b>Missing-original alerts.</b> If a published video has no safe copy, the dashboard tells you before it matters.</span></li>
          <li><Check/><span><b>Private restore links in 15 minutes.</b> Download one video or your whole catalogue and re-upload to a new channel.</span></li>
        </ul>
      </div>
      <div className="vault-demo" aria-label="Example of the protection dashboard">
        <div className="vault-demo-bar"><span>YOUR VAULT · @YOURCHANNEL</span><b>246 OF 247 PROTECTED</b></div>
        <div className="vault-demo-rows">
          {vaultRows.map(row => <div key={row.title} className={`vault-row ${row.status}`}>
            <span className="vault-thumb" aria-hidden="true"/>
            <div><strong>{row.title}</strong><small>Published {row.date}</small></div>
            {row.status === 'protected'
              ? <span className="vault-status ok"><Check/> Protected</span>
              : <span className="vault-status warn">Original missing</span>}
          </div>)}
        </div>
        <div className="vault-demo-footer"><span className="vault-restore">Restore all · 246 originals</span><small>SHA-256 verified · Export anytime</small></div>
      </div>
    </div>
  </section>
}

const faqs = [
  ['Do I have to upload videos myself?', 'No. You pick your video folders once. After that, the uploader backs up every new finished video automatically, every day. There is no “Back up now” button to remember — the whole point is that protection happens without you.'],
  ['What happens the day my channel is deleted?', 'Your vault is completely independent of YouTube. Your originals, titles, and descriptions stay exactly where they are. You generate private restore links, download your files, and re-upload to a new channel — days of work instead of years of loss.'],
  ['Where are my files stored, and can anyone see them?', 'In private object storage, encrypted in transit and at rest, verified byte-for-byte with SHA-256 after every upload. Nothing is public. Restore links exist only when you create them and expire after 15 minutes.'],
  ['Is this against YouTube’s terms?', 'No. Channel Backup never downloads anything from YouTube and never logs into your account. It protects the original files on your own computer — files you already own. The only YouTube data we read is your public channel catalog, to show you what is and isn’t protected.'],
  ['Why is “No Google login” a feature?', 'Because we never touch your YouTube account, we can never cause a strike, and a breach of us can never become a breach of your channel. You verify ownership with one temporary line in your channel description — no permission screens, ever.'],
  ['What if I cancel — or you shut down?', 'Your files are yours. You can export everything at any time, including after you cancel: your vault stays downloadable for 30 days. A backup service that locks your files hostage isn’t a backup service.'],
  ['What counts as one video?', 'One video up to 60 minutes — 5 GB on HD plans, 20 GB on 4K plans. Longer videos use one slot per hour, up to 4 hours per file. Four Shorts share a single slot.'],
  ['Should I pick HD or 4K?', 'Pick 4K if you export 4K masters — they’re about four times the size, which is why 4K plans cost about twice HD. HD plans accept videos up to 1080p. You can upgrade any time and your existing backups come with you.'],
]

function FaqSection() {
  return <section id="faq" className="faq-section section" data-reveal>
    <div className="section-heading centered"><span className="eyebrow">ASK US THE PARANOID QUESTIONS</span><h2>You’re buying certainty.<br/><em>Interrogate it.</em></h2></div>
    <div className="faq-grid">
      {faqs.map(([q, a]) => <details key={q} className="faq-item"><summary>{q}</summary><p>{a}</p></details>)}
    </div>
  </section>
}

function useReveals() {
  useEffect(() => {
    const nodes = [...document.querySelectorAll('[data-reveal]')]
    if (!('IntersectionObserver' in window)) { nodes.forEach(node => node.classList.add('is-visible')); return undefined }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target) }
    }), { threshold: 0.15, rootMargin: '0px 0px -8% 0px' })
    nodes.forEach(node => observer.observe(node))
    return () => observer.disconnect()
  }, [])
}

export default function LandingPage() {
  useReveals()
  return <div className="landing landing-v2">
    <header className="nav-shell nav-v2">
      <Logo />
      <nav><a href="#how">How it works</a><a href="#pricing">Pricing</a><a href="#faq">FAQ</a><Link to="/login">Log in</Link></nav>
      <PrimaryLink to="/signup?plan=creator&billing=monthly">Protect my videos <Arrow/></PrimaryLink>
    </header>

    <main>
      <section className="hero-v2 section">
        <div className="hero-v2-copy">
          <span className="eyebrow hero-kicker">PRIVATE DAILY BACKUPS FOR YOUTUBERS</span>
          <h1><span>Never let a lost channel</span><br/><em>erase your life&rsquo;s work.</em></h1>
          <p>A hack can lock you out. A strike can remove a channel overnight. Appeals cannot bring back an original file you no longer have. Channel Backup keeps a verified private copy of every original — automatically, every single day — so losing the channel never means losing the work.</p>
          <div className="hero-v2-actions"><PrimaryLink to="/signup?plan=creator&billing=monthly">Protect my videos <Arrow/></PrimaryLink><span>From <b>$29/month</b> for 100 videos.<br/>30-day money-back guarantee.</span></div>
          <p className="hero-v2-reassure">Set it up once. Backups run daily on their own — you never click “Back up”.</p>
        </div>
      </section>

      <section className="product-section section" data-reveal><BackupFlow/></section>

      <section id="how" className="how-v2" data-reveal>
        <div className="section how-v2-inner">
          <div className="section-heading"><span className="eyebrow">PAY. CONNECT. FORGET.</span><h2>Three steps between<br/>your work and <em>gone forever.</em></h2></div>
          <div className="steps-v2">
            <article><span>01</span><div><small>CHOOSE</small><h3>Pay for the vault you need.</h3><p>Start with 100, 300, or 1,000 videos in HD or 4K. We ask for payment before channel data.</p></div></article>
            <article><span>02</span><div><small>VERIFY</small><h3>Add your public handle.</h3><p>Prove ownership with one temporary line in your channel description. No Google permission screen, so we can never touch your channel.</p></div></article>
            <article><span>03</span><div><small>FORGET</small><h3>Pick your folder. That’s the last click.</h3><p>From then on the uploader backs up every new finished video daily and automatically, resumes interrupted copies, and verifies every stored byte.</p></div></article>
          </div>
        </div>
      </section>

      <RestoreProof/>

      <section className="comparison-v2 section" data-reveal>
        <div className="section-heading"><span className="eyebrow">THE HONEST COMPARISON</span><h2>A platform hosts your channel.<br/><em>A backup protects your files.</em></h2></div>
        <div className="comparison-table">
          <div className="comparison-row comparison-head"><span/><span>YOUTUBE</span><span>ONE HARD DRIVE</span><span className="ours">INFLUENCER BACKUPS</span></div>
          {[
            ['Keeps the original file', 'No', 'Yes', 'Yes'],
            ['Survives a lost channel', 'No', 'Yes', 'Yes'],
            ['Survives a failed drive', 'Yes', 'No', 'Yes'],
            ['Backs up new videos without you', 'No', 'No', 'Daily'],
            ['Checks every uploaded byte', 'No', 'No', 'SHA-256'],
            ['Creates a private restore link', 'No', 'No', '15 minutes'],
            ['Lets you export everything and leave', 'No', 'Yes', 'Anytime'],
          ].map(row => <div className="comparison-row" key={row[0]}>{row.map((cell, i) => <span className={i === 3 ? 'ours' : ''} key={i}>{i === 3 ? <Check/> : null}{cell}</span>)}</div>)}
        </div>
      </section>

      <section id="pricing" className="pricing-section section" data-reveal>
        <div className="section-heading centered"><span className="eyebrow">PRICING YOU CAN COUNT ON</span><h2>Choose how many videos<br/><em>must never disappear.</em></h2><p>Three plans, in HD or 4K. No storage maths. Payment comes before we ask for your channel.</p></div>
        <PricingCards />
      </section>

      <FaqSection/>

      <section className="final-cta final-v2 section" data-reveal><span className="eyebrow">THE NEXT UPLOAD NEEDS A SECOND HOME</span><h2>Back up the work.<br/><em>Keep the years.</em></h2><PrimaryLink to="/signup?plan=creator&billing=monthly">Protect my videos <Arrow/></PrimaryLink><p>100 videos from $29/month. Daily automatic backups. 30-day money-back guarantee.</p></section>
    </main>
    <Footer />
  </div>
}
